import {
  WXCC_ADDON_LABELS,
  formatAddOnItemCount,
  formatAddOnCurrency,
  formatFilteredAddOnItemCount,
} from "constant/wxcc-addons";
import { FC, ReactElement, ReactNode, useState } from "react";
import { Button, Drawer, IconButton, InputAdornment, TextField } from "@mui/material";
import { Close, Search } from "@mui/icons-material";
import {
  AddOnGroup,
  AddOnTotals,
  getGroupTotal,
} from "components/summary/index/Revenue/addonBreakdown";

import WxccAddOnPreview from "./WxccAddOnPreview";
import WxccAddOnCategoryBadge from "./WxccAddOnCategoryBadge";

interface WxccAddOnBreakdownProps {
  groups: AddOnGroup[];
  summaryTotals?: AddOnTotals;
  children?: (preview: ReactElement, drawerOpen: boolean) => ReactNode;
}

type AddOnDrawerCategory = AddOnGroup["category"] | typeof WXCC_ADDON_LABELS.all;
type AddOnDrawerGroup = Omit<AddOnGroup, "category"> & { category: AddOnDrawerCategory };

const WxccAddOnBreakdown: FC<WxccAddOnBreakdownProps> = ({
  groups: addOnGroups,
  summaryTotals,
  children,
}) => {
  const [drawerCategory, setDrawerCategory] = useState<AddOnDrawerCategory | null>(null);
  const [filter, setFilter] = useState("");
  const allAddOns = addOnGroups.flatMap((group) =>
    group.items.map((item) => ({
      ...item,
      id: group.category + ":" + item.id,
      category: group.category,
    }))
  );
  const drawerGroups: AddOnDrawerGroup[] = [
    {
      category: WXCC_ADDON_LABELS.all,
      color: "#357BA5",
      backgroundColor: "#EAF4FA",
      items: allAddOns,
    },
    ...addOnGroups,
  ];
  const drawerGroup = drawerGroups.find((group) => group.category === drawerCategory);
  const combinedTotal = getGroupTotal(allAddOns);
  const normalizedFilter = filter.normalize("NFKC").trim().toLowerCase();
  const filteredDrawerItems = allAddOns
    .filter(
      (item) =>
        (drawerCategory === WXCC_ADDON_LABELS.all || item.category === drawerCategory) &&
        item.label.normalize("NFKC").toLowerCase().includes(normalizedFilter)
    )
    .sort((left, right) => right.value - left.value);

  const openDrawer = (category: AddOnDrawerCategory) => {
    setFilter("");
    setDrawerCategory(category);
  };

  const closeDrawer = () => {
    setDrawerCategory(null);
  };

  const preview = (
    <WxccAddOnPreview
      groups={addOnGroups}
      summaryTotals={summaryTotals}
      onViewAll={openDrawer}
    />
  );

  // Keep the drawer mounted independently of a tooltip or search popper closing.
  return (
    <>
      {children ? children(preview, Boolean(drawerGroup)) : preview}
      <Drawer
        anchor="right"
        onClick={(event) => event.stopPropagation()}
        onClose={closeDrawer}
        open={Boolean(drawerGroup)}
        sx={{
          zIndex: 2100,
          "& .MuiBackdrop-root": {
            top: 64,
          },
        }}
        PaperProps={{
          sx: {
            boxSizing: "border-box",
            height: "calc(100% - 64px)",
            maxWidth: "100vw",
            top: 64,
            width: 560,
          },
        }}
      >
        {drawerGroup && (
          <div style={{ display: "flex", flexDirection: "column", height: "100%" }}>
            <div
              style={{
                alignItems: "flex-start",
                borderBottom: "1px solid #E1E6E9",
                display: "flex",
                justifyContent: "space-between",
                padding: "20px 24px 16px",
              }}
            >
              <div>
                <div style={{ color: "#23282C", fontSize: 18, fontWeight: 700 }}>
                  {WXCC_ADDON_LABELS.title}
                </div>
                <div style={{ color: "#70777A", fontSize: 12, marginTop: 5 }}>
                  {formatAddOnItemCount(allAddOns.length)} · {formatAddOnCurrency(combinedTotal)}{" "}
                  {WXCC_ADDON_LABELS.total}
                </div>
              </div>
              <IconButton
                aria-label={WXCC_ADDON_LABELS.closeDrawer}
                onClick={closeDrawer}
                size="small"
              >
                <Close />
              </IconButton>
            </div>
            <div
              style={{
                display: "grid",
                gap: 8,
                gridTemplateColumns: "auto repeat(2, minmax(0, 1fr))",
                padding: "16px 24px 0",
              }}
            >
              {drawerGroups.map((group) => {
                const selected = drawerGroup.category === group.category;
                const isAll = group.category === WXCC_ADDON_LABELS.all;

                return (
                  <Button
                    key={group.category}
                    aria-label={isAll ? WXCC_ADDON_LABELS.viewAllAria : group.category}
                    aria-pressed={selected}
                    onClick={() => openDrawer(group.category)}
                    size="small"
                    sx={{
                      alignItems: isAll ? "center" : "flex-start",
                      alignSelf: "stretch",
                      flexDirection: "column",
                      minWidth: isAll ? 44 : 0,
                      backgroundColor: selected ? group.backgroundColor : "#FFFFFF",
                      borderColor: selected ? group.color : "#CBD0D3",
                      color: selected ? group.color : "#535759",
                      fontSize: 12,
                      fontWeight: selected ? 700 : 500,
                      justifyContent: "center",
                      px: 1.25,
                      textTransform: "none",
                      "&:hover": {
                        backgroundColor: group.backgroundColor,
                        borderColor: group.color,
                      },
                    }}
                    variant="outlined"
                  >
                    <span>{isAll ? WXCC_ADDON_LABELS.allButton : group.category}</span>
                    {!isAll && (
                      <span style={{ fontSize: 11, whiteSpace: "nowrap" }}>
                        {formatAddOnItemCount(group.items.length)} ·{" "}
                        {formatAddOnCurrency(getGroupTotal(group.items))}
                      </span>
                    )}
                  </Button>
                );
              })}
            </div>
            <div style={{ padding: "18px 24px 12px" }}>
              <TextField
                fullWidth={true}
                onChange={(event) => setFilter(event.target.value)}
                placeholder={`${WXCC_ADDON_LABELS.filterPrefix} ${drawerGroup.category}`}
                size="small"
                value={filter}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search style={{ color: "#70777A", fontSize: 19 }} />
                    </InputAdornment>
                  ),
                  endAdornment: filter ? (
                    <InputAdornment position="end">
                      <IconButton
                        aria-label={WXCC_ADDON_LABELS.clearFilter}
                        edge="end"
                        onClick={() => setFilter("")}
                        size="small"
                      >
                        <Close style={{ fontSize: 17 }} />
                      </IconButton>
                    </InputAdornment>
                  ) : undefined,
                }}
              />
            </div>
            <div style={{ flex: 1, overflowY: "auto", padding: "0 24px 24px" }}>
              {addOnGroups.map((group) => {
                const items = filteredDrawerItems.filter(
                  (item) => item.category === group.category
                );
                if (!items.length) return null;

                return (
                  <div
                    key={group.category}
                    style={{ marginBottom: drawerCategory === WXCC_ADDON_LABELS.all ? 16 : 0 }}
                  >
                    {drawerCategory === WXCC_ADDON_LABELS.all && (
                      <div
                        style={{
                          alignItems: "center",
                          backgroundColor: "#F7F9FA",
                          borderRadius: 4,
                          color: "#333",
                          display: "flex",
                          fontSize: 12,
                          gap: 8,
                          padding: "8px 10px",
                        }}
                      >
                        <WxccAddOnCategoryBadge group={group} />
                        <span style={{ color: "#70777A" }}>
                          {formatAddOnItemCount(items.length)}
                        </span>
                        <span style={{ fontWeight: 700, marginLeft: "auto", whiteSpace: "nowrap" }}>
                          {formatAddOnCurrency(getGroupTotal(items))}
                        </span>
                      </div>
                    )}
                    {items.map((item) => (
                      <div
                        key={item.id}
                        style={{
                          alignItems: "start",
                          borderTop: "1px solid #EEF0F1",
                          display: "grid",
                          gap: 18,
                          gridTemplateColumns: "minmax(0, 1fr) auto",
                          padding: "10px 0",
                        }}
                      >
                        <span
                          style={{
                            color: "#535759",
                            fontSize: 12,
                            lineHeight: "16px",
                            overflowWrap: "anywhere",
                          }}
                        >
                          {item.label}
                        </span>
                        <span
                          style={{
                            color: "#333",
                            fontSize: 12,
                            fontWeight: 600,
                            whiteSpace: "nowrap",
                          }}
                        >
                          {formatAddOnCurrency(item.value)}
                        </span>
                      </div>
                    ))}
                  </div>
                );
              })}
              {filteredDrawerItems.length === 0 && (
                <div style={{ color: "#70777A", padding: "32px 0", textAlign: "center" }}>
                  {normalizedFilter ? WXCC_ADDON_LABELS.noMatches : WXCC_ADDON_LABELS.noItems}
                </div>
              )}
            </div>
            <div
              style={{
                alignItems: "center",
                background: "#FFFFFF",
                borderTop: "1px solid #E1E6E9",
                boxShadow: "0 -2px 8px rgba(0, 0, 0, 0.06)",
                display: "grid",
                flexShrink: 0,
                gap: 12,
                gridTemplateColumns: "minmax(0, 1fr) auto",
                padding: "12px 24px",
                position: "sticky",
                bottom: 0,
                zIndex: 2,
              }}
            >
              <div style={{ alignItems: "center", display: "flex", gap: 8 }}>
                <WxccAddOnCategoryBadge
                  group={drawerGroup}
                  variant="footer"
                />
                <span style={{ color: "#70777A", fontSize: 12 }}>
                  {normalizedFilter
                    ? formatFilteredAddOnItemCount(
                        filteredDrawerItems.length,
                        drawerGroup.items.length
                      )
                    : formatAddOnItemCount(drawerGroup.items.length)}
                </span>
              </div>
              <span style={{ color: "#333", fontSize: 13, fontWeight: 700 }}>
                {formatAddOnCurrency(getGroupTotal(drawerGroup.items))}
              </span>
            </div>
          </div>
        )}
      </Drawer>
    </>
  );
};

export default WxccAddOnBreakdown;
